import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatTable } from '@angular/material/table';
import { translate } from '@jsverse/transloco';
import { filter, Subject, takeUntil } from 'rxjs';
import { DropdownObject } from '@inmoment/the-kitchen';
import { RGBAColor } from '@inmoment/the-kitchen/src/lib/Pickers/ColorPicker/ColorPicker.model';

import { AppSharedModule } from 'app/app-shared.module';
import { ChartOptionConstants, Constants } from 'app/constants/constants';
import { FieldService } from 'app/providers/storage-api/field.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { minLengthArray } from 'app/utils/validator.utils';
import { MetadataField } from '../widget-add/widget-add.component';
import {
  NumericMetadataField,
  FieldForm,
  FieldFormGroup,
  FieldSelectorForm,
  FieldFormArray,
  FieldFormValues,
  Scoring,
  FieldType,
  PercentageSentiment,
} from './numeric-metadata-field-selector.models';
import {
  COMPONENT_CONSTANTS,
  getDefaultDecimalPrecision,
  isDocsField,
  isSentimentField,
} from './numeric-metadata-field-selector.utils';

@EnableClassTelemetry()
@Component({
  selector: 'app-numeric-metadata-field-selector',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [AppSharedModule],
  templateUrl: './numeric-metadata-field-selector.component.html',
  styleUrls: ['./numeric-metadata-field-selector.component.scss'],
})
export class NumericMetadataFieldSelectorComponent implements OnInit, OnChanges, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() fields?: MetadataField[];
  @Input() rows?: NumericMetadataField[];
  @Input() hasSettings = true;
  @Input() canModifyField = true;
  @Input() canSetColor = true;
  @Input() canSetDecimals = true;
  @Input() hasComparison?: boolean;
  @Input() colorPalette?: RGBAColor[];
  @Input() isReadOnly = false;
  @Input() sentimentScale?: number;
  @Input() journeyMode = false;

  @Output() valueChange = new EventEmitter<NumericMetadataField[]>();
  @Output() isValid = new EventEmitter<boolean>();

  @ViewChild('table') table: MatTable<FieldFormGroup>;

  displayedColumns = ['no', 'field'];
  isDragDisabled = true;
  form: FormGroup<FieldSelectorForm>;
  projectPalette: { color: RGBAColor }[];

  fieldSet: DropdownObject[];
  readonly decimalsSet: DropdownObject[] = ChartOptionConstants.DECIMALPRECISIONDOS;
  private readonly _scoringSetWithRating: DropdownObject[] = [
    { title: translate('widgets.numeric-metadata-field-selector.average'), id: 'avg' },
    { title: translate('widgets.numeric-metadata-field-selector.sum'), id: 'sum' },
    { title: translate('widgets.numeric-metadata-field-selector.top-box'), id: 'top' },
    { title: translate('widgets.numeric-metadata-field-selector.bottom-box'), id: 'bottom' },
    { title: translate('widgets.numeric-metadata-field-selector.nps'), id: 'nps' },
  ];
  private readonly _scoringSet = this._scoringSetWithRating.filter(
    (s) => s.id === 'avg' || s.id === 'sum'
  );

  private readonly _scoringSetForDocs: DropdownObject[] = [
    { title: translate('widgets.numeric-metadata-field-selector.sum'), id: 'sum' },
    { title: translate('widgets.numeric-metadata-field-selector.percentage'), id: 'percentage' },
  ];

  private readonly _scoringSetForSentiment: DropdownObject[] = [
    { title: translate('widgets.numeric-metadata-field-selector.average'), id: 'avg' },
    { title: translate('widgets.numeric-metadata-field-selector.percentage'), id: 'percentage' },
    { title: translate('widgets.numeric-metadata-field-selector.bar'), id: 'sentimentBar' },
  ];

  private readonly _scoringSetForJourney: DropdownObject[] = [
    { title: translate('widgets.numeric-metadata-field-selector.average'), id: 'AVERAGE' },
    { title: translate('widgets.numeric-metadata-field-selector.delta'), id: 'DELTA' },
    { title: translate('widgets.numeric-metadata-field-selector.final-value'), id: 'LAST' },
  ];

  readonly percentageSentiments3: DropdownObject[] = [
    { title: translate('widgets.numeric-metadata-field-selector.positive'), id: 'positive' },
    { title: translate('widgets.numeric-metadata-field-selector.neutral'), id: 'neutral' },
    { title: translate('widgets.numeric-metadata-field-selector.negative'), id: 'negative' },
  ];

  readonly percentageSentiments5: DropdownObject[] = [
    {
      title: translate('widgets.numeric-metadata-field-selector.very-positive'),
      id: 'very positive',
    },
    {
      title: translate('widgets.numeric-metadata-field-selector.somewhat-positive'),
      id: 'somewhat positive',
    },
    { title: translate('widgets.numeric-metadata-field-selector.neutral'), id: 'neutral' },
    {
      title: translate('widgets.numeric-metadata-field-selector.somewhat-negative'),
      id: 'somewhat negative',
    },
    {
      title: translate('widgets.numeric-metadata-field-selector.very-negative'),
      id: 'very negative',
    },
  ];

  percentageSentiments: DropdownObject[];

  private readonly _formBuilder = inject(FormBuilder);

  private readonly _destroy$ = new Subject<void>();

  constructor() {}

  ngOnInit() {
    this.initComponent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fields?.currentValue) {
      this.initFieldSet();
    }

    if (changes.colorPalette) {
      this.projectPalette = this.colorPalette.map((color) => ({
        color,
      }));
    }

    if (changes.isReadOnly) {
      this.updateReadOnlyState();
    }

    if (changes.sentimentScale?.currentValue) {
      this.percentageSentiments = this[`percentageSentiments${this.sentimentScale}`];
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  onDropped(event: CdkDragDrop<FieldFormGroup>): void {
    moveItemInArray(this.items.controls, event.previousIndex, event.currentIndex);

    this.items.updateValueAndValidity();

    this.table.renderRows();
  }

  onAddItem(): void {
    this.addNewItem();

    this.table.renderRows();
  }

  onDeleteItem(index: number): void {
    this.items.removeAt(index);

    this.table.renderRows();
  }

  hasMinMax(fieldFormGroup: FieldFormGroup): boolean {
    return ['top', 'bottom', 'nps'].includes(fieldFormGroup.controls.scoring.value);
  }

  getScoringSet(fieldFormGroup: FieldFormGroup): DropdownObject[] {
    if (this.journeyMode) {
      return this._scoringSetForJourney;
    }
    if (fieldFormGroup.controls.fieldType.value === 'rating') {
      return this._scoringSetWithRating;
    }
    if (isSentimentField(fieldFormGroup.controls.fieldId.value)) {
      return this._scoringSetForSentiment;
    }

    if (isDocsField(fieldFormGroup.controls.fieldId.value)) {
      return this._scoringSetForDocs;
    }

    return this._scoringSet;
  }

  getBoxSizeSet(fieldFormGroup: FieldFormGroup): DropdownObject[] {
    if (fieldFormGroup.controls.scoring.value === 'top') {
      return ChartOptionConstants.TOPBOXSIZEDOS;
    }

    return ChartOptionConstants.BOTTOMBOXSIZEDOS;
  }

  getFieldLongName(fieldFormGroup: FieldFormGroup): string {
    if (isDocsField(fieldFormGroup.controls.fieldId.value)) {
      return translate('widgets.numeric-metadata-field-selector.documents');
    }

    if (isSentimentField(fieldFormGroup.controls.fieldId.value)) {
      return translate('widgets.numeric-metadata-field-selector.sentiment');
    }

    return FieldService.getFieldLongName(fieldFormGroup.controls.fieldId.value);
  }

  get items(): FieldFormArray {
    return this.form?.controls?.items;
  }

  get canAddField(): boolean {
    return !this.hasComparison || !this.items.length;
  }

  private initComponent(): void {
    this.setDisplayedColumns();

    this.initForm();

    if (this.rows?.length) {
      this.loadInitialItems();
    } else {
      this.addNewItem();
    }
  }

  private initForm(): void {
    this.form = this._formBuilder.group<FieldSelectorForm>({
      items: this._formBuilder.array<FieldFormGroup>([], minLengthArray(1)),
    });

    this.setupFormListeners();
  }

  private setupFormListeners(): void {
    this.form.statusChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this.isValid.emit(this.form.valid));

    this.form.valueChanges
      .pipe(
        filter(() => this.form.valid),
        takeUntil(this._destroy$)
      )
      .subscribe(() => this.emitFormValues());
  }

  private initFieldSet(): void {
    this.fieldSet = this.fields
      .filter((f) => f.isDisplayed)
      .map((f) => ({
        title: f.label,
        id: f.name,
      }))
      .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'accent' }));

    if (!this.journeyMode) {
      this.fieldSet.unshift({
        title: translate('widgets.numeric-metadata-field-selector.documents'),
        id: COMPONENT_CONSTANTS.DOCS_ID,
      });
    }
  }

  private createFieldFormGroup(): FieldFormGroup {
    const color = this.colorPalette?.[this.items.length % this.colorPalette.length] ?? null;

    const fieldFormGroup = this._formBuilder.group<FieldForm>(
      {
        fieldId: new FormControl(null, Validators.required),
        fieldType: new FormControl(null),
        label: new FormControl({ value: '', disabled: true }, Validators.required),
        useDisplayName: new FormControl({ value: false, disabled: true }),
        min: new FormControl({ value: null, disabled: true }),
        max: new FormControl({ value: null, disabled: true }),
        scoring: new FormControl(null, Validators.required),
        percentageSentiment: new FormControl({ value: null, disabled: true }),
        boxSize: new FormControl({ value: null, disabled: true }),
        color: new FormControl(color),
        decimals: new FormControl(null),
        showOnTimeline: new FormControl(true),
      },
      { validators: this.minLessThanMaxValidator }
    );
    this.setupFieldFormGroupListeners(fieldFormGroup);

    return fieldFormGroup;
  }

  private minLessThanMaxValidator(group: FieldFormGroup): ValidationErrors | null {
    const min = group.controls.min.value;
    const max = group.controls.max.value;

    if (min === null || max === null) {
      return null;
    }

    if (min < max) {
      return null;
    }

    return { minNotLessThanMax: true };
  }

  private setupFieldFormGroupListeners(fieldFormGroup: FieldFormGroup): void {
    fieldFormGroup.controls.fieldId.valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe((fieldId) => this.handleFieldIdChange(fieldId, fieldFormGroup));

    fieldFormGroup.controls.scoring.valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe((scoring) => this.handleScoringChange(scoring, fieldFormGroup));

    fieldFormGroup.controls.label.valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe((label) => this.handleLabelChange(label, fieldFormGroup));

    fieldFormGroup.controls.useDisplayName.valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe((useDisplayName) =>
        this.handleUseDisplayNameChange(useDisplayName, fieldFormGroup)
      );
  }

  private handleFieldIdChange(fieldId: string, fieldFormGroup: FieldFormGroup): void {
    this.ensureLabelIsEnabled(fieldFormGroup);

    fieldFormGroup.patchValue(this.buildFieldFormValues(fieldId));
  }

  private ensureLabelIsEnabled(fieldFormGroup: FieldFormGroup): void {
    if (this.isReadOnly || !fieldFormGroup.controls.fieldId.value) {
      fieldFormGroup.controls.label.disable({ emitEvent: false });
      fieldFormGroup.controls.useDisplayName.disable({ emitEvent: false });
      return;
    }

    if (fieldFormGroup.controls.label.disabled) {
      fieldFormGroup.controls.label.enable({ emitEvent: false });
    }
    if (
      fieldFormGroup.controls.useDisplayName.disabled &&
      !isDocsField(fieldFormGroup.controls.fieldId.value) &&
      !isSentimentField(fieldFormGroup.controls.fieldId.value)
    ) {
      fieldFormGroup.controls.useDisplayName.enable({ emitEvent: false });
    }
  }

  private buildFieldFormValues(fieldId: string): Partial<FieldFormValues> {
    if (isDocsField(fieldId)) {
      return this.buildDocsFieldValues();
    }

    if (isSentimentField(fieldId)) {
      return this.buildSentimentFieldValues();
    }

    return this.buildRegularFieldValues(fieldId);
  }

  private buildDocsFieldValues(): Partial<FieldFormValues> {
    return {
      label: translate('widgets.numeric-metadata-field-selector.documents'),
      scoring: this.journeyMode ? 'AVERAGE' : 'sum',
      fieldType: null,
    };
  }

  private buildSentimentFieldValues(): Partial<FieldFormValues> {
    return {
      label: translate('widgets.numeric-metadata-field-selector.sentiment'),
      scoring: this.journeyMode ? 'AVERAGE' : 'avg',
      fieldType: null,
    };
  }

  private buildRegularFieldValues(fieldId: string): Partial<FieldFormValues> {
    const field = this.fields.find((f) => f.name === fieldId);
    return {
      label: FieldService.getFieldLongName(fieldId),
      scoring: this.journeyMode ? 'AVERAGE' : 'avg',
      fieldType: this.determineFieldType(field),
    };
  }

  private determineFieldType(field: MetadataField): FieldType {
    return field.isRating ? 'rating' : (field.type as 'long' | 'double');
  }

  private handleScoringChange(scoring: Scoring, fieldFormGroup: FieldFormGroup): void {
    this.resetMinMaxBoxSize(fieldFormGroup);

    this.configureScoringControls(scoring, fieldFormGroup);

    this.toggleMinMaxControls(fieldFormGroup);
  }

  private handleLabelChange(label: string, fieldFormGroup: FieldFormGroup): void {
    if (label === FieldService.getFieldLongName(fieldFormGroup.controls.fieldId.value)) {
      fieldFormGroup.controls.useDisplayName.setValue(true, { emitEvent: false });
    } else {
      fieldFormGroup.controls.useDisplayName.setValue(false, { emitEvent: false });
    }
  }

  private handleUseDisplayNameChange(
    useDisplayName: boolean,
    fieldFormGroup: FieldFormGroup
  ): void {
    const labelControl = fieldFormGroup.controls.label;
    if (useDisplayName) {
      labelControl.setValue(FieldService.getFieldLongName(fieldFormGroup.controls.fieldId.value));
    }
  }

  private resetMinMaxBoxSize(fieldFormGroup: FieldFormGroup): void {
    fieldFormGroup.patchValue(
      {
        min: null,
        max: null,
        boxSize: null,
      },
      { emitEvent: false }
    );

    fieldFormGroup.controls.min.clearValidators();
    fieldFormGroup.controls.max.clearValidators();
    fieldFormGroup.controls.boxSize.clearValidators();
    fieldFormGroup.controls.percentageSentiment.clearValidators();

    fieldFormGroup.controls.boxSize.disable({ emitEvent: false });
    fieldFormGroup.controls.percentageSentiment.disable({ emitEvent: false });
  }

  private configureScoringControls(scoring: Scoring, fieldFormGroup: FieldFormGroup): void {
    this.applyScoringDefaultValues(scoring, fieldFormGroup);

    this.setupScoringControlStates(scoring, fieldFormGroup);
  }

  private applyScoringDefaultValues(scoring: Scoring, fieldFormGroup: FieldFormGroup): void {
    switch (scoring) {
      case 'top':
      case 'bottom':
        this.applyTopBottomScoringDefaultValues(fieldFormGroup);
        break;
      case 'nps':
        this.applyNpsScoringDefaultValues(fieldFormGroup);
        break;
      case 'percentage':
        this.applyPercentageScoringDefaultValues(fieldFormGroup);
        break;
    }
  }

  private applyTopBottomScoringDefaultValues(fieldFormGroup: FieldFormGroup): void {
    fieldFormGroup.patchValue(
      {
        boxSize: Constants.TWTRA_DEFAULT_BOX_SIZE,
        min: COMPONENT_CONSTANTS.TOP_BOTTOM_SCORING_MIN_DEFAULT,
        max: COMPONENT_CONSTANTS.TOP_BOTTOM_SCORING_MAX_DEFAULT,
      },
      { emitEvent: false }
    );
  }

  private applyNpsScoringDefaultValues(fieldFormGroup: FieldFormGroup): void {
    fieldFormGroup.patchValue(
      {
        min: COMPONENT_CONSTANTS.NPS_SCORING_MIN_DEFAULT,
        max: COMPONENT_CONSTANTS.NPS_SCORING_MAX_DEFAULT,
      },
      { emitEvent: false }
    );
  }

  private applyPercentageScoringDefaultValues(fieldFormGroup: FieldFormGroup): void {
    if (isDocsField(fieldFormGroup.controls.fieldId.value)) {
      return;
    }
    const percentageSentiment = this.sentimentScale === 3 ? 'positive' : 'very positive';
    fieldFormGroup.patchValue(
      {
        percentageSentiment,
      },
      { emitEvent: false }
    );
  }

  private setupScoringControlStates(scoring: Scoring, fieldFormGroup: FieldFormGroup): void {
    switch (scoring) {
      case 'top':
      case 'bottom':
        this.setupTopBottomScoringControlStates(fieldFormGroup);
        break;
      case 'nps':
        this.setupNpsScoringControlStates(fieldFormGroup);
        break;
      case 'percentage':
        this.setupPercentageScoringControlStates(fieldFormGroup);
        break;
    }
  }

  private setupTopBottomScoringControlStates(fieldFormGroup: FieldFormGroup): void {
    fieldFormGroup.controls.boxSize.addValidators(Validators.required);
    fieldFormGroup.controls.min.addValidators([
      Validators.required,
      Validators.min(COMPONENT_CONSTANTS.TOP_BOTTOM_SCORING_MIN),
    ]);
    fieldFormGroup.controls.max.addValidators([
      Validators.required,
      Validators.max(COMPONENT_CONSTANTS.TOP_BOTTOM_SCORING_MAX),
    ]);

    fieldFormGroup.controls.boxSize.enable({ emitEvent: false });
  }

  private setupNpsScoringControlStates(fieldFormGroup: FieldFormGroup): void {
    fieldFormGroup.controls.min.addValidators([
      Validators.required,
      Validators.pattern(COMPONENT_CONSTANTS.NPS_SCORING_MIN_PATTERN),
    ]);
    fieldFormGroup.controls.max.addValidators([
      Validators.required,
      Validators.max(COMPONENT_CONSTANTS.NPS_SCORING_MAX),
    ]);
  }

  private setupPercentageScoringControlStates(fieldFormGroup: FieldFormGroup): void {
    if (isDocsField(fieldFormGroup.controls.fieldId.value)) {
      return;
    }
    fieldFormGroup.controls.percentageSentiment.addValidators(Validators.required);
    fieldFormGroup.controls.percentageSentiment.enable({ emitEvent: false });
  }

  private toggleMinMaxControls(fieldFormGroup: FieldFormGroup): void {
    const shouldEnable = this.hasMinMax(fieldFormGroup);
    const minControl = fieldFormGroup.controls.min;
    const maxControl = fieldFormGroup.controls.max;

    if (shouldEnable) {
      minControl.enable({ emitEvent: false });
      maxControl.enable({ emitEvent: false });
      return;
    }

    minControl.disable({ emitEvent: false });
    maxControl.disable({ emitEvent: false });
  }

  private loadInitialItems(): void {
    this.rows.forEach((row) => {
      if (row.useDisplayName !== true) {
        row.useDisplayName = row.label === FieldService.getFieldLongName(row.fieldId);
      }

      const fieldFormGroup = this.createFieldFormGroup();

      fieldFormGroup.patchValue(this.mapRowToFormValues(row), { emitEvent: false });

      this.setupInitialControlStates(fieldFormGroup, row);

      this.items.push(fieldFormGroup, { emitEvent: false });
    });

    this.items.updateValueAndValidity();
    this.updateReadOnlyState();
  }

  private mapRowToFormValues(row: NumericMetadataField): Partial<FieldFormValues> {
    return {
      fieldId: row.fieldId,
      fieldType: row.fieldType,
      label: row.useDisplayName ? FieldService.getFieldLongName(row.fieldId) : row.label,
      useDisplayName: row.useDisplayName,
      min: row.min ? parseInt(row.min, 10) : null,
      max: row.max ? parseInt(row.max, 10) : null,
      scoring: row.scoring as Scoring,
      percentageSentiment: row.percentageSentiment as PercentageSentiment,
      boxSize: row.boxSize,
      color: row.color,
      decimals:
        row.decimals ?? (this.canSetDecimals ? getDefaultDecimalPrecision(row.fieldId) : null),
      showOnTimeline: row.showOnTimeline ?? false,
    };
  }

  private setupInitialControlStates(
    fieldFormGroup: FieldFormGroup,
    row: NumericMetadataField
  ): void {
    this.ensureLabelIsEnabled(fieldFormGroup);

    this.setupScoringControlStates(row.scoring as Scoring, fieldFormGroup);

    this.toggleMinMaxControls(fieldFormGroup);
  }

  private emitFormValues(): void {
    const metadataFields: NumericMetadataField[] = this.form.getRawValue().items.map((i) => ({
      fieldId: i.fieldId,
      fieldType: i.fieldType,
      label: i.label,
      useDisplayName: i.useDisplayName,
      min: i.min?.toString(),
      max: i.max?.toString(),
      scoring: i.scoring,
      percentageSentiment: i.percentageSentiment,
      boxSize: i.boxSize,
      color: i.color,
      decimals: i.decimals,
      showOnTimeline: i.showOnTimeline,
    }));

    this.valueChange.emit(metadataFields);
  }

  private setDisplayedColumns(): void {
    if (this.journeyMode) {
      this.displayedColumns.push(...COMPONENT_CONSTANTS.JOURNEY_COLUMNS);
    } else if (this.hasSettings) {
      this.displayedColumns.push(...COMPONENT_CONSTANTS.SETTINGS_COLUMNS);
    }

    if (this.canSetDecimals) {
      this.displayedColumns.push(COMPONENT_CONSTANTS.DECIMALS_COLUMN);
    }

    if (this.canSetColor) {
      this.displayedColumns.push(COMPONENT_CONSTANTS.COLOR_COLUMN);
    }

    if (this.canModifyField) {
      this.displayedColumns.push(COMPONENT_CONSTANTS.ACTION_COLUMN);
    }
  }

  private addNewItem(): void {
    this.items.push(this.createFieldFormGroup());
    this.updateReadOnlyState();
  }

  private updateReadOnlyState(): void {
    if (!this.items) {
      return;
    }
    this.items.controls.forEach((control) => this.ensureLabelIsEnabled(control));
  }
}
